import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LINKS = [
  { to: "/", label: "Anasayfa" },
  { to: "/hizmetler", label: "Hizmetler" },
  { to: "/hesaplama", label: "GES Hesaplama" },
  { to: "/projeler", label: "Projeler" },
  { to: "/hakkimizda", label: "Hakkımızda" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,backdrop-filter,border-color,padding] duration-300 ${
          scrolled || open
            ? "bg-black/70 backdrop-blur-xl border-b border-white/10 py-3"
            : "bg-transparent border-b border-transparent py-5"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
            <img src="/logo-dark.png" alt="Renewolt Enerji" className="h-9 sm:h-11 w-auto object-contain" />
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                className={({ isActive }) =>
                  `relative px-4 py-2 text-sm font-medium tracking-tight transition-colors duration-200 ${
                    isActive ? "text-[#5CC528]" : "text-neutral-300 hover:text-white"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    {isActive && (
                      <motion.span
                        layoutId="nav-underline"
                        className="absolute left-3 right-3 -bottom-0.5 h-px bg-[#5CC528]"
                      />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/iletisim"
              data-testid="nav-cta-contact"
              className="hidden sm:inline-flex items-center gap-1.5 bg-[#5CC528] text-black text-sm font-semibold pl-5 pr-4 py-2.5 rounded-full hover:bg-[#4AA020] transition-colors duration-200"
            >
              İletişime Geç <ArrowUpRight size={16} />
            </Link>
            <button
              onClick={() => setOpen((o) => !o)}
              data-testid="mobile-menu-toggle"
              className="lg:hidden text-white p-2"
              aria-label="Menü"
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            data-testid="mobile-menu"
            className="fixed top-[68px] left-0 right-0 z-40 lg:hidden bg-black/95 backdrop-blur-xl border-b border-white/10"
          >
            <nav className="flex flex-col px-5 py-4">
              {LINKS.concat({ to: "/iletisim", label: "İletişim" }).map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  className={({ isActive }) =>
                    `py-3.5 text-lg font-medium tracking-tight border-b border-white/5 ${
                      isActive ? "text-[#5CC528]" : "text-neutral-200"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
